import { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  CheckCircle,
  Loader2,
  UserCircle,
  AlertCircle,
} from 'lucide-react';
import { useAuth } from 'context/AuthContext';
import { reportAuthError } from 'utils/reportError';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from 'config/firebase';
import Input from 'components/forms/Input';

// ============================================================================
// Animations
// ============================================================================

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// ============================================================================
// Styled Components
// ============================================================================

const PageWrapper = styled.div`
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.offWhite};
  padding: 1rem;

  ${({ theme }) => theme.mediaQueries.md} {
    padding: 2rem;
  }
`;

const FormContainer = styled.div`
  background: white;
  padding: 2rem 1.5rem;
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  width: 100%;
  max-width: 400px;
  text-align: center;
  animation: ${fadeIn} 0.6s ease-out;

  @media (min-width: 480px) {
    padding: 2.5rem 2rem;
    max-width: 440px;
  }

  ${({ theme }) => theme.mediaQueries.md} {
    padding: 3rem 2.5rem;
    max-width: 480px;
  }
`;

const Form = styled.form`
  width: 100%;
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;

  ${({ theme }) => theme.mediaQueries.md} {
    margin-bottom: 2rem;
  }

  svg {
    color: ${({ theme }) => theme.colors.primaryDark};
    filter: drop-shadow(0 4px 8px rgba(178, 34, 34, 0.2));
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.primaryDark};
  letter-spacing: -0.02em;

  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 2.25rem;
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 2rem;
  font-size: 1.125rem;
  line-height: 1.5;

  ${({ theme }) => theme.mediaQueries.md} {
    margin-bottom: 2.5rem;
    font-size: 1.25rem;
  }
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
  text-align: left;

  ${({ theme }) => theme.mediaQueries.md} {
    margin-bottom: 1.75rem;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  input {
    padding-right: 3.5rem !important;

    ${({ theme }) => theme.mediaQueries.md} {
      padding-right: 4rem !important;
    }
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 1.25rem;
  z-index: 2;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ theme }) => theme.mediaQueries.md} {
    right: 1.5rem;
    padding: 0.625rem;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.secondary};
    background: ${({ theme }) => theme.colors.secondaryLight};
  }

  &:focus {
    outline: none;
    color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.secondaryLight};
  }
`;

const SuccessText = styled.div`
  color: ${({ theme }) => theme.colors.success};
  font-size: 1.125rem;
  margin: 1.5rem 0;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.success}10;
  border-radius: 8px;
  border-left: 4px solid ${({ theme }) => theme.colors.success};
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  animation: ${slideDown} 0.3s ease-out;
  text-align: left;
  line-height: 1.5;

  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 1.25rem;
    margin: 2rem 0;
    padding: 1.125rem;
  }

  svg {
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
`;

const ErrorText = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: 1.125rem;
  margin: 1.5rem 0;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.error}10;
  border-radius: 8px;
  border-left: 4px solid ${({ theme }) => theme.colors.error};
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  animation: ${slideDown} 0.3s ease-out;
  text-align: left;
  line-height: 1.5;

  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 1.25rem;
    margin: 2rem 0;
    padding: 1.125rem;
  }

  svg {
    flex-shrink: 0;
    margin-top: 0.125rem;
  }
`;

const MessageContainer = styled.div`
  margin: 1.5rem 0;

  ${({ theme }) => theme.mediaQueries.md} {
    margin: 2rem 0;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 1.25rem;
  background-color: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.primaryDark};
  font-weight: 700;
  letter-spacing: 0.03em;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  min-height: 60px;
  box-shadow: ${({ theme }) =>
    `0 6px 20px ${theme.colors.secondary}30, inset 0 1px 0 rgba(255,255,255,0.12)`};

  ${({ theme }) => theme.mediaQueries.md} {
    padding: 1.375rem;
    font-size: 1.375rem;
    margin-bottom: 2rem;
    min-height: 64px;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => `0 8px 24px ${theme.colors.secondary}40`};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }

  svg {
    animation: ${(props) =>
      props.$loading
        ? css`
            ${spin} 1s linear infinite
          `
        : 'none'};
  }
`;

const LinkSection = styled.div`
  margin-top: 1.5rem;
  font-size: 1.125rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  ${({ theme }) => theme.mediaQueries.md} {
    margin-top: 2rem;
    gap: 1.5rem;
    font-size: 1.25rem;
  }
`;

const ForgotPassword = styled.div`
  a {
    color: ${({ theme }) => theme.colors.primaryDark};
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s ease;
    padding: 0.75rem;
    border-radius: 8px;
    display: inline-block;
    font-size: 1.125rem;

    ${({ theme }) => theme.mediaQueries.md} {
      padding: 0.875rem;
      font-size: 1.25rem;
    }

    &:hover {
      color: ${({ theme }) => theme.colors.primary};
      background: ${({ theme }) => theme.colors.offWhite};
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.secondaryLight};
    }
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: ${({ theme }) => theme.colors.primaryDark};
  font-size: 1.25rem;

  ${({ theme }) => theme.mediaQueries.md} {
    gap: 1.25rem;
    font-size: 1.375rem;
  }

  svg {
    animation: ${css`
      ${spin} 1s linear infinite
    `};
  }
`;

// Custom styled Input for larger text that matches the design
const StyledInput = styled(Input)`
  input {
    font-size: 1.25rem !important;
    padding: 1.25rem 1.25rem !important;
    min-height: 60px;

    ${({ theme }) => theme.mediaQueries.md} {
      font-size: 1.375rem !important;
      padding: 1.375rem 1.5rem !important;
      min-height: 64px;
    }

    &::placeholder {
      font-size: 1.125rem !important;

      ${({ theme }) => theme.mediaQueries.md} {
        font-size: 1.25rem !important;
      }
    }
  }

  label {
    font-size: 1.125rem !important;
    margin-bottom: 0.75rem !important;

    ${({ theme }) => theme.mediaQueries.md} {
      font-size: 1.25rem !important;
      margin-bottom: 0.875rem !important;
    }
  }

  /* Error message styling */
  span[role='alert'] {
    font-size: 1.125rem !important;
    margin-top: 0.75rem !important;

    ${({ theme }) => theme.mediaQueries.md} {
      font-size: 1.25rem !important;
      margin-top: 1rem !important;
    }
  }
`;

// ============================================================================
// Main Component
// ============================================================================

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const navigate = useNavigate();
  const { user, authError, loading, resetPassword } = useAuth();
  const emailRef = useRef(null);

  // Focus email field on mount
  useEffect(() => {
    if (emailRef.current) {
      emailRef.current.focus();
    }
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    if (user && !loading) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate]);

  // Display auth errors from context
  useEffect(() => {
    if (authError) {
      setError(authError);
      setIsLoading(false);
      setSuccessMessage('');
    }
  }, [authError]);

  // Clear errors when the user edits a field. This used to fire whenever an
  // error was set while the fields held text — i.e. always, right after a
  // failed submit — so no sign-in error was ever visible (found via #479).
  const lastCredentials = useRef({ email, password });
  useEffect(() => {
    const edited =
      lastCredentials.current.email !== email ||
      lastCredentials.current.password !== password;
    lastCredentials.current = { email, password };
    if (edited && error) {
      setError('');
    }
  }, [email, password, error]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const errors = {};

    if (!email) {
      errors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address first');
      if (emailRef.current) {
        emailRef.current.focus();
      }
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      if (emailRef.current) {
        emailRef.current.focus();
      }
      return;
    }

    // Clear any existing messages
    setError('');
    setIsLoading(true);

    try {
      const result = await resetPassword(email);
      if (result.success) {
        setSuccessMessage(
          'Password reset instructions sent to your email. Please check your inbox and spam folder.',
        );
      } else {
        setError(
          result.error ||
            'Failed to send password reset email. Please try again.',
        );
      }
    } catch (err) {
      reportAuthError(err, { component: 'Login', action: 'resetPassword' });
      setError(
        err.message || 'Failed to send password reset email. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous messages
    setError('');
    setSuccessMessage('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Auth state change will trigger redirection in useEffect
    } catch (err) {
      // User-caused codes (wrong password, unknown email) stay quiet; anything
      // else — including the `default` branch below — is ours to see (#479).
      reportAuthError(err, { component: 'Login', action: 'signIn' });
      // Provide user-friendly error messages
      let errorMessage = 'Failed to log in. Please try again.';

      switch (err.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          errorMessage =
            'Invalid email or password. Please check your credentials and try again.';
          break;
        case 'auth/too-many-requests':
          errorMessage =
            'Too many failed login attempts. Please try again later or reset your password.';
          break;
        case 'auth/user-disabled':
          errorMessage =
            'This account has been disabled. Please contact support for assistance.';
          break;
        case 'auth/network-request-failed':
          errorMessage =
            'Network error. Please check your internet connection and try again.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address format.';
          break;
        default:
          errorMessage = `Login failed: ${err.message}`;
      }

      setError(errorMessage);
      setIsLoading(false);
    }
  };

  // Show loading overlay while checking authentication
  if (loading) {
    return (
      <LoadingOverlay>
        <LoadingSpinner>
          <Loader2 size={40} />
          <div>Loading...</div>
        </LoadingSpinner>
      </LoadingOverlay>
    );
  }

  return (
    <PageWrapper>
      <FormContainer>
        <Form onSubmit={handleSubmit} noValidate>
          <Logo>
            <UserCircle size={60} />
          </Logo>

          <Title>Welcome back!</Title>
          <Subtitle>Log in to your account to continue</Subtitle>

          <InputGroup>
            <StyledInput
              ref={emailRef}
              type="email"
              label="Email Address"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={fieldErrors.email}
              required
              autoComplete="email"
              data-testid="email-input"
            />
          </InputGroup>

          <InputGroup>
            <PasswordInputWrapper>
              <StyledInput
                type={showPassword ? 'text' : 'password'}
                label="Password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={fieldErrors.password}
                required
                autoComplete="current-password"
                data-testid="password-input"
              />
              <ToggleButton
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </ToggleButton>
            </PasswordInputWrapper>
          </InputGroup>

          <MessageContainer>
            {error && (
              <ErrorText>
                <AlertCircle size={18} />
                <div>{error}</div>
              </ErrorText>
            )}

            {successMessage && (
              <SuccessText>
                <CheckCircle size={18} />
                <div>{successMessage}</div>
              </SuccessText>
            )}
          </MessageContainer>

          <Button
            type="submit"
            disabled={isLoading}
            $loading={isLoading}
            data-testid="login-button"
          >
            {isLoading ? (
              <>
                <Loader2 size={20} />
                Logging in...
              </>
            ) : (
              'Log in'
            )}
          </Button>

          <LinkSection>
            <ForgotPassword>
              <a href="#" onClick={handleResetPassword}>
                Forgot your password?
              </a>
            </ForgotPassword>
          </LinkSection>
        </Form>
      </FormContainer>
    </PageWrapper>
  );
}
